import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import FileUpload from "../../components/FileUpload";
import { getFuelLogDetails, updateFuelFinanceStatus } from "../../store/Slices/FuelSlice";
import SpinnerLoader from "../../utils/SpinnerLoader";
import { actionIcon, cashIcon, checkedIcon, claimedVolumeIcon, closedIcon, driverClaimPriceIcon, foodBeveragesIcon, maintenanceRepairIcon, miscellaneousIcon, resolvedIcon, sensorIcon, tollTaxesIcon, variableIcon, varianceIcon } from "../../utils/svgs";
import { errorNotify, successNotify } from "../../utils/toast";
import FuelCard from "./FuelCard";

const FuelFinance = () => {
    const dispatch = useDispatch();
    const [activeTab, setActiveTab] = useState('pettyCash');
    const [uploadedFiles, setUploadedFiles] = useState([]);

    const { getFuelLogDetailsData, getFuelLogDetailsLoading } = useSelector((state) => state.fuel);

    const handleFinanceStatus = async (status) => {
        try {
            const payload = {
                trip_id: getFuelLogDetailsData?.id,
                finance_status: status
            }
            await dispatch(updateFuelFinanceStatus(payload)).unwrap();
            successNotify("Finance status updated successfully");
            dispatch(getFuelLogDetails(getFuelLogDetailsData?.id));
        } catch (error) {
            errorNotify(error?.message || "Failed to update finance status");
        }
    }

    useEffect(() => {
        if (getFuelLogDetailsData?.fuel_files?.length > 0) {
            setUploadedFiles(
                getFuelLogDetailsData?.fuel_files?.map((img) => ({
                    fileName: img.file_path,
                    originalName: img.file_path,
                    previewUrl: `${process.env.REACT_APP_API_FILES_URL}${img.file_path}`,
                    backend: true,
                    id: img.id
                }))
            );
        }

        return () => {
            setUploadedFiles([]);
        }
    }, [getFuelLogDetailsData])

    if (getFuelLogDetailsLoading) {
        return <SpinnerLoader />
    }

    const fuelReconciliationItems = [
        {
            icon: driverClaimPriceIcon,
            label: "Driver Claim Price",
            unit: "PKR",
            value: getFuelLogDetailsData?.fuel_reconciliation?.fuelClaimedPrice,
            valueColor: "text-primary"
        },
        {
            icon: claimedVolumeIcon,
            label: "Claimed Volume",
            unit: "ltrs",
            value: getFuelLogDetailsData?.fuel_reconciliation?.fuelClaimedVolume
        },
        {
            icon: sensorIcon,
            label: "Sensor Data",
            unit: "ltrs",
            value: getFuelLogDetailsData?.fuel_reconciliation?.fuelSensorData || "--",
            subValue: ""
        },
        {
            icon: variableIcon,
            label: "Remaining",
            unit: "PKR",
            value: getFuelLogDetailsData?.fuel_reconciliation?.variance,
            valueColor: "text-danger",
            isHighlight: true
        }
    ];

    // const pettyCashItems = [
    //     {
    //         icon: cashIcon,
    //         label: "Petty Cash Expense Claim",
    //         unit: "PKR",
    //         value: getFuelLogDetailsData?.claimed_petty_cash
    //     },
    //     {
    //         icon: cashIcon,
    //         label: "Allocated Petty Cash",
    //         unit: "PKR",
    //         value: getFuelLogDetailsData?.allocated_petty_cash
    //     },
    //     {
    //         icon: varianceIcon,
    //         label: "Remaining",
    //         unit: "PKR",
    //         value: getFuelLogDetailsData?.variance_petty_cash,
    //         valueColor: `${getFuelLogDetailsData?.variance_petty_cash < 0 ? "text-danger" : "text-success"}`,
    //         isHighlight: true
    //     }
    // ];

    const pettyCashItems = [
        {
            icon: cashIcon,
            label: "Expense Claim",
            unit: "PKR",
            value: getFuelLogDetailsData?.total_trip_driver_expenses?.total_expense || 0 // default to 0 if undefined
        },
        {
            icon: cashIcon,
            label: "Allocated Petty Cash",
            unit: "PKR",
            value: getFuelLogDetailsData?.allocated_petty_cash || 0 // default to 0 if undefined
        },
        {
            icon: varianceIcon,
            label: "Remaining",
            unit: "PKR",
            value: (getFuelLogDetailsData?.allocated_petty_cash || 0) - (getFuelLogDetailsData?.total_trip_driver_expenses?.total_expense || 0),
            valueColor: (getFuelLogDetailsData?.allocated_petty_cash || 0) - (getFuelLogDetailsData?.total_trip_driver_expenses?.total_expense || 0) < 0
                ? "text-danger"
                : "text-success",
            isHighlight: true
        }
    ];

    const getExpenseIcon = (title) => {
        if (!title) return miscellaneousIcon;
        const lowerTitle = title.toLowerCase();
        if (lowerTitle.includes("toll")) return tollTaxesIcon;
        if (lowerTitle.includes("maintenance")) return maintenanceRepairIcon;
        if (lowerTitle.includes("food")) return foodBeveragesIcon;
        return miscellaneousIcon;
    };

    const expensesItems = (getFuelLogDetailsData?.total_trip_driver_expenses?.expenses || []).map(expense => ({
        icon: getExpenseIcon(expense.title),
        label: expense.title,
        value: expense.total_amount
    }));

    const pettyCashTabs = [
        {
            label: "Petty Cash",
            active: activeTab === 'pettyCash',
            onClick: () => setActiveTab('pettyCash')
        },
        {
            label: "Expenses",
            active: activeTab === 'expenses',
            onClick: () => setActiveTab('expenses')
        }
    ];

    return (
        <div className="fuel_finance_main">
            {
                (!getFuelLogDetailsData?.finance_status || getFuelLogDetailsData?.finance_status === "pending") &&
                <div className="fuel_approval_main mb-3">
                    <div>
                        {actionIcon}
                        Action: Please review the finance reconciliation and approve or reject it. This action will be permanently logged.
                    </div>

                    <div className="work_order_status">
                        <div className="d-flex align-items-center gap-2">
                            <button type="button" onClick={() => handleFinanceStatus("approved")}>
                                {resolvedIcon} Approve
                            </button>
                            <button type="button" onClick={() => handleFinanceStatus("rejected")}>
                                {closedIcon} Reject
                            </button>
                        </div>
                    </div>
                </div>
            }

            <Row>
                <Col md={6}>
                    <FuelCard
                        title="Fuel Reconciliation"
                        subtitle="Fuel Information for this trip record."
                        items={fuelReconciliationItems}
                        checkedIcon={getFuelLogDetailsData?.finance_status === "approved" ? checkedIcon : null}
                    />
                </Col>
                <Col md={6}>
                    <FuelCard
                        title="Petty Cash Reconciliation"
                        subtitle="Petty Cash Information for this trip record."
                        tabs={pettyCashTabs}
                        items={activeTab === 'pettyCash' ? pettyCashItems : expensesItems}
                        checkedIcon={getFuelLogDetailsData?.finance_status === "approved" ? checkedIcon : null}
                    />
                </Col>
                <Col md={12}>
                    <div className="content_box">
                        <h6>Attachments</h6>
                        <p> You can attach up to 5 files, max 1MB each.</p>

                        <div className="photo_document_section" style={{ border: "none", padding: "0" }}>
                            <FileUpload
                                destination="fuel-logs"
                                files={uploadedFiles}
                                setFiles={setUploadedFiles}
                                maxFileSize={10}
                                tripId={getFuelLogDetailsData?.id}
                            />
                        </div>
                    </div>
                </Col>
            </Row>
        </div>
    );
};

export default FuelFinance;